import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Card as ToolkitCard } from '@quantum-ecosystem/ui-toolkit';

const spacingMap = {
	xs: 8,
	sm: 12,
	md: 16,
	lg: 24,
	xl: 32,
};

const withSpacing = (val) => {
	if (val == null) return undefined;
	if (typeof val === 'number') return `${val}px`;
	if (spacingMap[val] !== undefined) return `${spacingMap[val]}px`;
	return val;
};

const FormCard = forwardRef(({
	p,
	px,
	py,
	pt,
	pb,
	pl,
	pr,
	m,
	mx,
	my,
	mt,
	mb,
	ml,
	mr,
	w,
	h,
	miw,
	minWidth,
	maw,
	maxWidth,
	mih,
	minHeight,
	mah,
	maxHeight,
	pos,
	flex,
	display,
	withBorder,
	shadow,
	radius,
	bg,
	c,
	style,
	className = '',
	hoverable = false,
	children,
	...props
}, ref) => {
	const computedStyle = {
		padding: withSpacing(p),
		paddingLeft: withSpacing(px || pl),
		paddingRight: withSpacing(px || pr),
		paddingTop: withSpacing(py || pt),
		paddingBottom: withSpacing(py || pb),
		margin: withSpacing(m),
		marginLeft: withSpacing(mx || ml),
		marginRight: withSpacing(mx || mr),
		marginTop: withSpacing(my || mt),
		marginBottom: withSpacing(my || mb),
		width: withSpacing(w),
		height: withSpacing(h),
		minWidth: withSpacing(miw ?? minWidth),
		maxWidth: withSpacing(maw ?? maxWidth),
		minHeight: withSpacing(mih ?? minHeight),
		maxHeight: withSpacing(mah ?? maxHeight),
		position: pos,
		flex,
		display,
		backgroundColor: bg,
		color: c,
		...style,
	};

	Object.keys(computedStyle).forEach(
		(key) => computedStyle[key] === undefined && delete computedStyle[key]
	);

	return (
		<ToolkitCard
			ref={ref}
			hoverable={hoverable}
			className={className}
			style={computedStyle}
			{...props}
		>
			{children}
		</ToolkitCard>
	);
});

FormCard.displayName = 'FormCard';

FormCard.propTypes = {
	p: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	px: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	py: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	pt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	pb: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	pl: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	pr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	m: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	mx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	my: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	mt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	mb: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	ml: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	mr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	w: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	h: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	miw: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	maw: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	mih: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	mah: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	pos: PropTypes.string,
	flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	display: PropTypes.string,
	withBorder: PropTypes.bool,
	shadow: PropTypes.string,
	radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	bg: PropTypes.string,
	c: PropTypes.string,
	style: PropTypes.object,
	className: PropTypes.string,
	hoverable: PropTypes.bool,
	children: PropTypes.node,
};

export default FormCard;
